import { impactosAte, pertenceAoOcupante, type ImpactoMedido } from './impactosMedidos'

export type RegiaoCorporalAtencao = 'cabeca' | 'torax' | 'pelve'
export type NivelAtencaoCorporal = 'observacao' | 'atencao' | 'alto'

export type ImpactoParaAtencao = ImpactoMedido

export interface AtencaoCorporal {
  regiao: RegiaoCorporalAtencao
  nivel: NivelAtencaoCorporal
  alvo: string
  velocidadeRelativaKmh: number
  /** instante em que o contato foi medido, na régua 0..1 */
  tempo: number
  leitura: string
}

const regiaoDoNome = (nome: string): RegiaoCorporalAtencao | null => {
  const normalizado = nome.toLocaleLowerCase('pt-BR')
  if (normalizado.includes('cabeça') || normalizado.includes('cabeca')) return 'cabeca'
  if (normalizado.includes('tronco') || normalizado.includes('tórax') || normalizado.includes('torax')) return 'torax'
  if (normalizado.includes('pelve') || normalizado.includes('coxas')) return 'pelve'
  return null
}

const nivelDaVelocidade = (kmh: number): NivelAtencaoCorporal =>
  kmh >= 30 ? 'alto' : kmh >= 15 ? 'atencao' : 'observacao'

/**
 * Converte contatos medidos em regiões de atenção. Não infere diagnóstico:
 * sem contato registrado, não colore a região; com contato, descreve somente
 * mecanismo, alvo e intensidade relativa.
 *
 * `tempo` é obrigatório e não tem padrão. A lista de impactos cobre o evento
 * inteiro, e sem o instante as regiões acendiam com a régua em zero — o boneco
 * "machucava" assim que começava a se deslocar dentro do carro, antes de o
 * veículo encostar em nada. Deixar o parâmetro opcional só adiaria o defeito
 * para o próximo consumidor que esquecesse de passá-lo.
 */
export function avaliarAtencaoCorporal(
  impactos: readonly ImpactoParaAtencao[],
  idOcupante: string,
  tempo: number,
): AtencaoCorporal[] {
  const porRegiao = new Map<RegiaoCorporalAtencao, AtencaoCorporal>()

  for (const impacto of impactosAte(impactos, tempo)) {
    if (!pertenceAoOcupante(impacto.id, idOcupante)) continue
    const regiao = regiaoDoNome(impacto.nomeCorpo)
    if (!regiao) continue
    const atual = porRegiao.get(regiao)
    if (atual && atual.velocidadeRelativaKmh >= impacto.velocidadeRelativaKmh) continue
    const nivel = nivelDaVelocidade(impacto.velocidadeRelativaKmh)
    porRegiao.set(regiao, {
      regiao,
      nivel,
      alvo: impacto.alvo,
      velocidadeRelativaKmh: impacto.velocidadeRelativaKmh,
      tempo: impacto.tempo,
      leitura:
        `${regiao === 'cabeca' ? 'Cabeça' : regiao === 'torax' ? 'Tórax' : 'Pelve'} em atenção: ` +
        `contato com ${impacto.alvo.toLocaleLowerCase('pt-BR')} a ` +
        `${impacto.velocidadeRelativaKmh.toFixed(0)} km/h em relação ao veículo. ` +
        'Indica mecanismo a investigar, não diagnóstico de lesão.',
    })
  }

  return [...porRegiao.values()]
}
